import {
  PostStatus,
  STATUS_COMPLETED,
} from '@kontentino/kontentino-constants/Posts';
import { ApiClientError, get, post } from 'api/client';
import { ShiftPostsVariant } from 'app/modules/posts/hooks/useShiftPosts';
import { isPostWorkflowApiNew } from 'app/config/postWorkflowApiMode';
import config from 'config';
import { Post } from 'types/Post';
import { ApprovalData } from 'types/PostWorkflow';
import { getExtendedTimeout } from 'utils/request';

export type PostWorkflowBatchFailure = {
  post_id: number;
  error_code?: string;
  message?: string;
};

export type PostWorkflowBatchResult = {
  succeeded: number[];
  failed: PostWorkflowBatchFailure[];
};

function assertNoBatchFailures(result: PostWorkflowBatchResult): void {
  if (result.failed.length > 0) {
    throw new ApiClientError({
      message: 'Post workflow request partially failed',
      userMessage: result.failed[0]?.message,
      response: result,
    });
  }
}

type AssignTasksParams = {
  posts: number[];
  users: number[];
  message: string;
  isUrgent: boolean;
  canSendEmail: boolean;
};

export type SendForApprovalParams = {
  type: ApprovalType;
  posts: number[];
  users: number[];
  additionalUsers?: string;
  subject: string;
  message: string;
};

export type ChangeStatusParams = {
  posts: number[];
  status: PostStatus;
};

export type ShiftToProjectParams = {
  ids: number[];
  projectId: number;
  cloneAsDraft?: boolean;
  variant: ShiftPostsVariant;
  shiftDays?: number;
  postsCustomSchedule?: Record<
    number,
    {
      customScheduleDate?: string;
      customScheduleTime?: string;
    }
  >;
};

export type MovePostRequest = {
  projectId: number;
  date?: string;
  time?: string;
  shiftDays?: number;
};

export type MovePostResponse = Post & {
  warning?: string | null;
};

export type MovePostSettledResult = {
  postId: number;
} & (
  | { status: 'fulfilled'; value: MovePostResponse }
  | { status: 'rejected'; reason: string }
);

const PostTimeSaversApi = {
  async assignTasks(params: AssignTasksParams): Promise<Post[]> {
    if (isPostWorkflowApiNew()) {
      const result = await post<PostWorkflowBatchResult>(
        'posts/assign-task',
        {
          post_ids: params.posts,
          assignee_user_ids: params.users,
          text: params.message,
          is_urgent: params.isUrgent,
          is_send_notification_on: params.canSendEmail,
        },
        { baseURL: config.API_SERVICE_ENDPOINT },
      );

      assertNoBatchFailures(result);

      return [];
    }

    const data = new FormData();
    const urgentValue = params.isUrgent ? '1' : '0';
    const notificationValue = params.canSendEmail ? '1' : '0';

    data.append('postIds', JSON.stringify(params.posts));
    data.append('userIds', JSON.stringify(params.users));
    data.append('text', params.message);
    data.append('isUrgent', urgentValue);
    data.append('isSendNotificationOn', notificationValue);
    data.append('extended', '1');

    return post('/posts2/ajaxAssignMassTasks', data);
  },
  async movePosts(params: {
    ids: number[];
    projectId: number;
    shiftDays?: number;
    postsCustomSchedule?: Record<
      number,
      {
        customScheduleDate?: string;
        customScheduleTime?: string;
      }
    >;
  }): Promise<MovePostSettledResult[]> {
    const results = await Promise.allSettled(
      params.ids.map((id) => {
        const postSchedule = params.postsCustomSchedule?.[id];

        const body: MovePostRequest = {
          projectId: params.projectId,
        };

        if (postSchedule?.customScheduleDate) {
          body.date = postSchedule.customScheduleDate;
        }

        if (postSchedule?.customScheduleTime) {
          body.time = postSchedule.customScheduleTime;
        }

        if (!postSchedule && params.shiftDays) {
          body.shiftDays = params.shiftDays;
        }

        return post(`/posts/${id}/move`, body, {
          baseURL: config.API_SERVICE_ENDPOINT,
          timeout: getExtendedTimeout(10),
        }) as Promise<MovePostResponse>;
      }),
    );

    return results.map((result, index) => {
      const postId = params.ids[index];

      if (result.status === 'fulfilled') {
        return { postId, status: 'fulfilled' as const, value: result.value };
      }

      const error = result.reason as ApiClientError;
      return {
        postId,
        status: 'rejected' as const,
        reason: error?.userMessage || error?.message || 'Unknown error',
      };
    });
  },
  shiftToProject(
    params: ShiftToProjectParams,
  ): Promise<Post[] | MovePostSettledResult[]> {
    if (params.variant === 'duplicate') {
      return PostTimeSaversApi.duplicatePosts(params);
    }

    if (params.variant === 'move') {
      return PostTimeSaversApi.movePosts({
        ids: params.ids,
        projectId: params.projectId,
        shiftDays: params.shiftDays,
        postsCustomSchedule: params.postsCustomSchedule,
      });
    }

    return Promise.all(
      params.ids.map((id) => {
        const data = new FormData();

        data.append('id', id.toString());
        data.append('projectId', params.projectId.toString());

        if (params.cloneAsDraft) {
          data.append('forceStatus', `${STATUS_COMPLETED}`);
        }

        return post('/posts/ajaxClonePostToAnotherContentPlan', data, {
          timeout: getExtendedTimeout(),
        }) as Promise<Post>;
      }),
    );
  },
  saveApprovalMessageBody(params: { type: ApprovalType; value: string }) {
    const data = new FormData();

    data.append('type', params.type);
    data.append('value', params.value);

    return post('/dashboard/ajaxUpdateApprovalBody', data);
  },
  duplicatePosts(params: ShiftToProjectParams): Promise<Post[]> {
    return Promise.all(
      params.ids.map((id) => {
        const data = new FormData();

        data.append('id', id.toString());
        data.append('projectId', params.projectId.toString());

        const postCustomSchedule = params.postsCustomSchedule?.[id];

        if (postCustomSchedule?.customScheduleDate) {
          data.append(
            'customScheduleDate',
            postCustomSchedule.customScheduleDate,
          );
        }

        if (postCustomSchedule?.customScheduleTime) {
          data.append(
            'customScheduleTime',
            postCustomSchedule.customScheduleTime,
          );
        }

        if (params.cloneAsDraft) {
          data.append('forceStatus', `${STATUS_COMPLETED}`);
        }

        return post(`/posts/${id}/duplicate`, data, {
          baseURL: config.API_SERVICE_ENDPOINT,
          timeout: getExtendedTimeout(10),
        }) as Promise<Post>;
      }),
    );
  },

  async changeStatus(
    params: ChangeStatusParams,
  ): Promise<PostWorkflowBatchResult> {
    if (isPostWorkflowApiNew()) {
      return post<PostWorkflowBatchResult>(
        'posts/mass-change-status',
        {
          ids: params.posts,
          newStatus: params.status,
        },
        { baseURL: config.API_SERVICE_ENDPOINT },
      );
    }

    await post(
      '/posts2/ajaxMassChangePostsStatus',
      (() => {
        const data = new FormData();
        data.append('newStatus', params.status.toString());
        data.append('ids', JSON.stringify(params.posts));
        return data;
      })(),
    );

    return { succeeded: params.posts, failed: [] };
  },
  async sendForApproval(
    params: SendForApprovalParams,
  ): Promise<{ status: boolean }> {
    if (isPostWorkflowApiNew()) {
      const additionalRecipientEmails = params.additionalUsers?.length
        ? params.additionalUsers
            .split(',')
            .map((email) => email.trim())
            .filter(Boolean)
        : [];

      const result = await post<PostWorkflowBatchResult>(
        'posts/send-for-approval',
        {
          type: params.type,
          post_ids: params.posts,
          recipient_user_ids: params.users,
          email_subject: params.subject,
          email_body: params.message,
          additional_recipient_emails: additionalRecipientEmails,
        },
        { baseURL: config.API_SERVICE_ENDPOINT },
      );

      assertNoBatchFailures(result);

      return { status: true };
    }

    const data = new FormData();

    data.append('type', params.type);
    data.append('emailSubject', params.subject);
    data.append('emailBody', params.message);
    data.append('selectedPosts', JSON.stringify(params.posts));
    data.append('selectedUsers', JSON.stringify(params.users));

    if (!!params.additionalUsers?.length) {
      data.append('additionalUsers', params.additionalUsers);
    }

    return post('/posts2/ajaxSendPostsForApproval', data);
  },
  getApprovalData(pages: number[], type: ApprovalType): Promise<ApprovalData> {
    const modifyResponse = (res: ApprovalData<{ surname: string }>) => ({
      ...res,
      users: res.users.map((user) => ({
        ...user,
        name: `${user.name} ${user.surname}`,
      })),
    });

    if (isPostWorkflowApiNew()) {
      return get<ApprovalData<{ surname: string }>>('posts/approval-data', {
        baseURL: config.API_SERVICE_ENDPOINT,
        params: {
          'page_ids[]': pages.join(),
          type,
        },
      }).then(modifyResponse);
    }

    return get<ApprovalData<{ surname: string }>>(
      'posts/ajaxRetrievePostApprovalData',
      {
        params: {
          'page_ids[]': pages.join(),
          type,
        },
      },
    ).then(modifyResponse);
  },
};

export default PostTimeSaversApi;
